<script lang="ts" setup>
import {
  computed,
  inject,
  ref,
  useAttrs,
  watch,
} from 'vue'
import type { FieldGroupProvider, FieldInputWrapperProvider, FieldProvider } from '../../types/field'
import { DTFieldGroupProvider, DTFieldInputWrapperProvider, DTFieldProvider } from '../../keys'
import type { ComplexClassName } from '../../types/common'
import { classArray } from '../../utils/class'
import type { FieldInputSizes, FieldInputType } from './types'

type ModelValue = string | number

defineOptions({
  inheritAttrs: false,
})

const props = withDefaults(defineProps<{
  modelValue?: ModelValue
  disabled?: boolean
  type?: FieldInputType
  sizeVariant?: keyof typeof FieldInputSizes
  invalid?: boolean
  pattern?: string
  id?: string
  required?: boolean
  wrapperClass?: ComplexClassName
}>(), {
  disabled: false,
  type: 'text',
})

const emit = defineEmits<{
  (e: 'focusout', event: FocusEvent): void
  (e: 'update:modelValue', event: ModelValue): void
}>()

const input = ref<HTMLInputElement>()

defineExpose({
  el: input,
  $el: input,
})

const touched = ref(false)
const onFocusout = (ev: FocusEvent) => {
  touched.value = true
  emit('focusout', ev)
}
const onInput = (ev: Event) => {
  emit('update:modelValue', (ev.target as HTMLInputElement).value)
}

const labelProvider = inject<FieldProvider | undefined>(DTFieldProvider, undefined)
watch(() => props.required, (required) => {
  if (labelProvider)
    labelProvider.hasRequiredInput = required
}, { immediate: true })

const attrs = useAttrs()
const inputId = computed(() => props?.id ?? labelProvider?.inputId)
const descriptionId = computed(() => labelProvider?.descriptionId)
const boundAttrs = computed(() =>
  ({
    'placeholder': ' ',
    ...attrs,
    'disabled': props.disabled,
    'id': inputId.value,
    'aria-describedby': descriptionId.value,
    'required': props.required,
    onFocusout,
    onInput,
  }),
)

const groupProvider = inject<FieldGroupProvider | undefined>(DTFieldGroupProvider, undefined)
const inputWrapperProvider = inject<FieldInputWrapperProvider | undefined>(DTFieldInputWrapperProvider, undefined)

const sizeClass = computed<string>(() => {
  const size = props?.sizeVariant ?? inputWrapperProvider?.size ?? groupProvider?.size ?? labelProvider?.size
  switch (size) {
    case 'xs':
      return 'py-2 px-3 text-xs'
    case 'sm':
      return 'py-2 px-3 text-sm'
    case 'lg':
      return 'py-4 px-4.5'
    case 'md':
    default:
      return 'text-xs sm:text-sm py-2 px-3 md:text-base md:py-2.5 md:px-3.5'
  }
})

const inputClass = computed<string[]>(() => {
  const cls = [
    'rounded-[inherit]',
    'grow',
    'focus:outline-none',
  ]
  cls.push(sizeClass.value)

  return cls
})

const wrapperClasses = computed<string[]>(() => {
  const cls = [
    'text-gray-800',
    'rounded-lg',
    'border',
    'focus-within:border-gray-800',
    'antialiased',
    'inline-flex',
  ]

  if (props.disabled)
    cls.push('border-gray-300/40 text-gray-800/40 [&>input]:bg-transparent')

  else
    cls.push('border-gray-300')

  if (props.invalid)
    cls.push('border-red-300 focus:border-red-500')

  if (touched.value) {
    cls.push(
      'invalid:[&:not(:focus-within)]:border-red-300 invalid:focus-within:border-red-500',
    )
  }

  if (props.wrapperClass)
    cls.push(...classArray(props.wrapperClass))

  return cls
})

const inputPattern = computed(() => props.pattern ?? props.type === 'tel' ? '^\\+(?:[0-9] ?){6,14}[0-9]$' : undefined)
</script>

<template>
  <!-- eslint-disable tailwindcss/no-custom-classname -->
  <fieldset
    :class="wrapperClasses"
    class="dt-field-input"
  >
    <!-- eslint-enable tailwindcss/no-custom-classname -->
    <slot
      :input-attrs="boundAttrs"
      :input-class="inputClass"
    >
      <input
        ref="input"
        :value="modelValue"
        v-bind="boundAttrs"
        :class="inputClass"
        :type="type"
        :pattern="inputPattern"
        :required="required"
      >
    </slot>
  </fieldset>
</template>
